import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import OnboardingProgressModel from '../../../../../shared/models/onboarding/api/OnboardingProgressModel';
import Button from '../../../../../shared/components/general/Button';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import ConfirmationDialog from '../../../../../shared/components/structure/ConfirmationDialog';
import {
    bulkUpdateOnboardingEmailTriggerStatus,
    OnboardingEmailTriggerId,
    OnboardingEmailTriggerStatus
} from '../../../../../shared/api/controllers/onboarding';
import { OutboundCallFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import { parseOnboardingProgressDetails } from '../../../../onboarding/utils/onboardingProgressDetails';

export interface LocationEmailTriggerDefinition {
    triggerId: OnboardingEmailTriggerId;
    label: string;
    description: string;
    isAutomated: boolean;
    group: 'inbound' | 'outbound';
}

export const inboundLocationEmailTriggers: LocationEmailTriggerDefinition[] = [
    {
        triggerId: 'loc-launched',
        label: 'Location Onboarding Invitation',
        description: 'Triggered: Manually by staff to invite client',
        isAutomated: false,
        group: 'inbound'
    },
    {
        triggerId: 'loc-reminder',
        label: 'Location Onboarding Reminder',
        description: 'Triggered: Automated reminder if onboarding form is incomplete',
        isAutomated: true,
        group: 'inbound'
    },
    {
        triggerId: 'testing-ready',
        label: 'Testing Invitation',
        description: 'Triggered: Once internal engineering and automated QA complete',
        isAutomated: false,
        group: 'inbound'
    },
    {
        triggerId: 'phase2-testing-ready',
        label: 'Round 2 Testing Invitation',
        description: 'Triggered: Once Round 1 feedback is resolved and fixes deployed',
        isAutomated: false,
        group: 'inbound'
    },
    {
        triggerId: 'launch-ready',
        label: 'Launch Ready Notification',
        description: 'Triggered: Once all testing rounds are completed by client',
        isAutomated: false,
        group: 'inbound'
    },
    {
        triggerId: 'go-live',
        label: 'Go-Live Notification',
        description: 'Triggered: Once voice assistant goes live in production',
        isAutomated: false,
        group: 'inbound'
    }
];

export const outboundLocationEmailTriggers: LocationEmailTriggerDefinition[] = [
    {
        triggerId: 'outbound-launched',
        label: 'Outbound Onboarding Invitation',
        description: 'Triggered: Manually by staff to invite client',
        isAutomated: false,
        group: 'outbound'
    },
    {
        triggerId: 'outbound-reminder',
        label: 'Outbound Onboarding Reminder',
        description: 'Triggered: Automated reminder if outbound setup is incomplete',
        isAutomated: true,
        group: 'outbound'
    },
    {
        triggerId: 'outbound-submitted',
        label: 'Outbound Onboarding Submitted',
        description: 'Triggered: Automated when office manager submits outbound form',
        isAutomated: true,
        group: 'outbound'
    }
];

export const locationEmailTriggers = [...inboundLocationEmailTriggers, ...outboundLocationEmailTriggers];

export interface BulkEmailTriggerLocation extends ApplicationModel {
    id: string;
    name: string;
}

export interface BulkEmailTriggerLocationState {
    application: BulkEmailTriggerLocation;
    progress?: OnboardingProgressModel | null;
    loading?: boolean;
    error?: string;
}

export interface LocationEmailTriggerDefaults {
    skippedTriggerIds: OnboardingEmailTriggerId[];
    appliedApplicationIdsByTrigger: Record<string, string[]>;
    updatedAt?: string;
}

type BulkEmailAction = 'skip' | 'reset';
type DialogStep = 'configure' | 'review' | 'result';

interface TriggerCounts {
    Pending: number;
    Sent: number;
    Skipped: number;
    applicable: number;
    eligible: number;
}

interface LocationChange {
    application: BulkEmailTriggerLocation;
    triggerIds: OnboardingEmailTriggerId[];
}

interface FailedLocation {
    applicationId: string;
    applicationName: string;
    error: string;
}

interface OperationResult {
    totalLocations: number;
    totalChanges: number;
    successfulApplicationIds: string[];
    failures: FailedLocation[];
    policyError?: string;
}

interface BulkEmailTriggerManagerDialogProps {
    locations: BulkEmailTriggerLocationState[];
    onClose: () => void;
    onProgressUpdated: (application: BulkEmailTriggerLocation, progress: OnboardingProgressModel) => void;
    onPersistentDefaultsChange?: (
        action: BulkEmailAction,
        triggerIds: OnboardingEmailTriggerId[],
        appliedApplicationIds: string[],
        targetApplicationIds: string[]
    ) => Promise<string | null>;
}

export const supportsOutboundEmailTriggers = (application: ApplicationModel): boolean => {
    return !!application.featureFlags?.some(flag => flag.id === OutboundCallFeatureFlagId);
};

export const getLocationEmailTriggerStatus = (
    progress: OnboardingProgressModel | null | undefined,
    triggerId: OnboardingEmailTriggerId
): OnboardingEmailTriggerStatus => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    return details.emailTriggers?.[triggerId]?.status || 'Pending';
};

const isTriggerApplicable = (
    application: ApplicationModel,
    definition: LocationEmailTriggerDefinition
): boolean => definition.group === 'inbound' || supportsOutboundEmailTriggers(application);

export const getUnappliedDefaultTriggerIds = (
    defaults: LocationEmailTriggerDefaults | null | undefined,
    application: ApplicationModel
): OnboardingEmailTriggerId[] => {
    if (!defaults) return [];
    return (defaults.skippedTriggerIds || []).filter(triggerId => {
        const definition = locationEmailTriggers.find(item => item.triggerId === triggerId);
        if (!definition || !isTriggerApplicable(application, definition)) return false;
        return !(defaults.appliedApplicationIdsByTrigger?.[triggerId] || []).includes(application.id);
    });
};

export const updateLocationEmailTriggerDefaults = (
    currentDefaults: Partial<LocationEmailTriggerDefaults> | null | undefined,
    action: BulkEmailAction,
    triggerIds: OnboardingEmailTriggerId[],
    appliedApplicationIds: string[],
    targetApplicationIds: string[]
): LocationEmailTriggerDefaults => {
    const currentSkippedTriggerIds = currentDefaults?.skippedTriggerIds || [];
    const skippedTriggerIds = action === 'skip'
        ? Array.from(new Set([...currentSkippedTriggerIds, ...triggerIds]))
        : currentSkippedTriggerIds.filter(id => !triggerIds.includes(id));
    const currentAppliedByTrigger = currentDefaults?.appliedApplicationIdsByTrigger || {};
    const appliedApplicationIdsByTrigger = { ...currentAppliedByTrigger };

    triggerIds.forEach(triggerId => {
        if (action === 'reset') {
            delete appliedApplicationIdsByTrigger[triggerId];
            return;
        }
        const retainedApplicationIds = (currentAppliedByTrigger[triggerId] || [])
            .filter(applicationId => !targetApplicationIds.includes(applicationId));
        appliedApplicationIdsByTrigger[triggerId] = Array.from(new Set([
            ...retainedApplicationIds,
            ...appliedApplicationIds
        ]));
    });

    return {
        skippedTriggerIds,
        appliedApplicationIdsByTrigger: skippedTriggerIds.length > 0 ? appliedApplicationIdsByTrigger : {},
        updatedAt: new Date().toISOString()
    };
};

export const isEmailTriggerEligible = (
    status: OnboardingEmailTriggerStatus,
    action: BulkEmailAction
): boolean => action === 'skip' ? status === 'Pending' : status === 'Skipped';

export const getActionableCountTooltip = (counts: TriggerCounts, action: BulkEmailAction): string => {
    if (counts.applicable === 0) {
        return 'None of the selected locations support this email trigger.';
    }

    const locationLabel = counts.applicable === 1 ? 'selected location' : 'selected locations';
    const parts = action === 'skip'
        ? [`${counts.Pending} of ${counts.applicable} ${locationLabel} ${counts.Pending === 1 ? 'is' : 'are'} pending and can be skipped.`]
        : [`${counts.Skipped} of ${counts.applicable} ${locationLabel} ${counts.Skipped === 1 ? 'is' : 'are'} skipped and can be restored to pending.`];

    if (action === 'skip' && counts.Skipped > 0) {
        parts.push(`${counts.Skipped} ${counts.Skipped === 1 ? 'is' : 'are'} already skipped.`);
    }
    if (action === 'reset' && counts.Pending > 0) {
        parts.push(`${counts.Pending} ${counts.Pending === 1 ? 'is' : 'are'} already pending and won't change.`);
    }
    if (counts.Sent > 0) {
        parts.push(`${counts.Sent} ${counts.Sent === 1 ? 'was' : 'were'} already sent and won't change.`);
    }

    return parts.join(' ');
};

const runWithConcurrency = async <T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> => {
    const results: Array<Promise<R>> = [];
    const executing = new Set<Promise<R>>();

    for (const item of items) {
        const promise = Promise.resolve().then(() => fn(item));
        results.push(promise);
        executing.add(promise);
        const clean = () => executing.delete(promise);
        promise.then(clean, clean);
        if (executing.size >= limit) {
            await Promise.race(executing);
        }
    }

    return Promise.all(results);
};

const BulkEmailTriggerManagerDialog: React.FC<BulkEmailTriggerManagerDialogProps> = ({
    locations,
    onClose,
    onProgressUpdated,
    onPersistentDefaultsChange
}) => {
    const nonLiveLocations = useMemo(() => locations
        .filter(location => !(location.application as any).isLive), [locations]);
    const liveLocationsCount = locations.length - nonLiveLocations.length;

    const availableLocationIds = useMemo(() => nonLiveLocations
        .filter(location => !location.loading && !location.error)
        .map(location => location.application.id), [nonLiveLocations]);
    const [step, setStep] = useState<DialogStep>('configure');
    const [action, setAction] = useState<BulkEmailAction>('skip');
    const [activeGroup, setActiveGroup] = useState<'inbound' | 'outbound'>('inbound');
    const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>(availableLocationIds);
    const [selectedTriggerIds, setSelectedTriggerIds] = useState<OnboardingEmailTriggerId[]>([]);
    const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [applyToFutureLocations, setApplyToFutureLocations] = useState(true);
    const [isApplying, setIsApplying] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [result, setResult] = useState<OperationResult | null>(null);
    const selectAllLocationsRef = useRef<HTMLInputElement>(null);
    const selectAllTriggersRef = useRef<HTMLInputElement>(null);

    const selectedLocations = useMemo(() => nonLiveLocations.filter(location =>
        selectedApplicationIds.includes(location.application.id)
        && !location.loading
        && !location.error
    ), [nonLiveLocations, selectedApplicationIds]);

    const hasOutboundLocations = selectedLocations.some(location => supportsOutboundEmailTriggers(location.application));

    useEffect(() => {
        if (activeGroup === 'outbound' && !hasOutboundLocations) {
            setActiveGroup('inbound');
        }
    }, [activeGroup, hasOutboundLocations]);

    const triggerCounts = useMemo(() => locationEmailTriggers.reduce((counts, definition) => {
        const nextCounts = { Pending: 0, Sent: 0, Skipped: 0, applicable: 0, eligible: 0 };
        selectedLocations.forEach(location => {
            if (!isTriggerApplicable(location.application, definition)) return;
            const status = getLocationEmailTriggerStatus(location.progress, definition.triggerId);
            nextCounts[status] += 1;
            nextCounts.applicable += 1;
            if (isEmailTriggerEligible(status, action)) nextCounts.eligible += 1;
        });
        counts[definition.triggerId] = nextCounts;
        return counts;
    }, {} as Record<OnboardingEmailTriggerId, TriggerCounts>), [selectedLocations, action]);

    const applicationChanges = useMemo(() => selectedLocations.reduce((changes, location) => {
        const triggerIds = selectedTriggerIds.filter(triggerId => {
            const definition = locationEmailTriggers.find(item => item.triggerId === triggerId);
            if (!definition || !isTriggerApplicable(location.application, definition)) return false;
            return isEmailTriggerEligible(getLocationEmailTriggerStatus(location.progress, triggerId), action);
        });
        if (triggerIds.length > 0) {
            changes.push({ application: location.application, triggerIds });
        }
        return changes;
    }, [] as LocationChange[]), [selectedLocations, selectedTriggerIds, action]);

    const totalChanges = applicationChanges.reduce((total, change) => total + change.triggerIds.length, 0);
    const activeDefinitions = activeGroup === 'inbound' ? inboundLocationEmailTriggers : outboundLocationEmailTriggers;
    const selectableActiveIds = activeDefinitions
        .filter(definition => triggerCounts[definition.triggerId]?.eligible > 0)
        .map(definition => definition.triggerId);
    const selectedActiveCount = selectableActiveIds.filter(id => selectedTriggerIds.includes(id)).length;
    const allActiveSelected = selectableActiveIds.length > 0 && selectedActiveCount === selectableActiveIds.length;
    const allAvailableLocationsSelected = availableLocationIds.length > 0
        && availableLocationIds.every(id => selectedApplicationIds.includes(id));

    useEffect(() => {
        if (selectAllLocationsRef.current) {
            selectAllLocationsRef.current.indeterminate = selectedApplicationIds.length > 0 && !allAvailableLocationsSelected;
        }
    }, [selectedApplicationIds, allAvailableLocationsSelected]);

    useEffect(() => {
        if (selectAllTriggersRef.current) {
            selectAllTriggersRef.current.indeterminate = selectedActiveCount > 0 && !allActiveSelected;
        }
    }, [selectedActiveCount, allActiveSelected]);

    useEffect(() => {
        if (!allAvailableLocationsSelected) {
            setApplyToFutureLocations(false);
        } else {
            setApplyToFutureLocations(true);
        }
    }, [allAvailableLocationsSelected]);

    const filteredLocations = nonLiveLocations.filter(location =>
        location.application.name.toLowerCase().includes(locationSearch.trim().toLowerCase())
    );

    const toggleTrigger = (triggerId: OnboardingEmailTriggerId) => {
        setSelectedTriggerIds(current => current.includes(triggerId)
            ? current.filter(id => id !== triggerId)
            : [...current, triggerId]);
    };

    const toggleLocation = (applicationId: string) => {
        setSelectedApplicationIds(current => current.includes(applicationId)
            ? current.filter(id => id !== applicationId)
            : [...current, applicationId]);
    };

    const applyChanges = async (changes: LocationChange[], isRetry: boolean) => {
        if (isApplying || changes.length === 0) return;
        setIsApplying(true);
        setCompletedCount(0);

        const successes: string[] = [];
        const failures: FailedLocation[] = [];

        await runWithConcurrency(changes, 5, async change => {
            try {
                const response = await bulkUpdateOnboardingEmailTriggerStatus(change.application.id, {
                    triggerIds: change.triggerIds,
                    action
                });
                if (response.resultType === 'Ok' && response.data) {
                    successes.push(change.application.id);
                    onProgressUpdated(change.application, response.data);
                } else {
                    failures.push({
                        applicationId: change.application.id,
                        applicationName: change.application.name,
                        error: response.errors?.[0] || 'Failed to update email triggers.'
                    });
                }
            } catch (error) {
                failures.push({
                    applicationId: change.application.id,
                    applicationName: change.application.name,
                    error: error?.message || 'Failed to update email triggers.'
                });
            } finally {
                setCompletedCount(current => current + 1);
            }
        });

        let policyError: string | undefined;
        if (applyToFutureLocations && onPersistentDefaultsChange) {
            const failedApplicationIds = failures.map(failure => failure.applicationId);
            const appliedApplicationIds = selectedApplicationIds.filter(id => !failedApplicationIds.includes(id));
            policyError = await onPersistentDefaultsChange(
                action,
                selectedTriggerIds,
                appliedApplicationIds,
                selectedApplicationIds
            ) || undefined;
        }

        setResult(previous => {
            if (isRetry && previous) {
                return {
                    ...previous,
                    successfulApplicationIds: [...previous.successfulApplicationIds, ...successes],
                    failures,
                    policyError
                };
            }
            return {
                totalLocations: changes.length,
                totalChanges: changes.reduce((total, change) => total + change.triggerIds.length, 0),
                successfulApplicationIds: successes,
                failures,
                policyError
            };
        });
        setStep('result');
        setIsApplying(false);
    };

    const retryFailedLocations = () => {
        if (!result) return;
        const failedIds = result.failures.map(failure => failure.applicationId);
        applyChanges(applicationChanges.filter(change => failedIds.includes(change.application.id)), true);
    };

    const retryFutureDefaults = async () => {
        if (!result || !onPersistentDefaultsChange || isApplying) return;
        setIsApplying(true);
        const error = await onPersistentDefaultsChange(
            action,
            selectedTriggerIds,
            selectedApplicationIds,
            selectedApplicationIds
        );
        setResult(current => current ? { ...current, policyError: error || undefined } : current);
        setIsApplying(false);
    };

    const renderFooter = () => {
        if (step === 'configure') {
            return (
                <FooterActions>
                    <Button themes={['white', 'secondary']} text="Cancel" type="button" onClick={onClose} />
                    <Button
                        themes={['primary']}
                        text="Review changes"
                        type="button"
                        disabled={totalChanges === 0}
                        onClick={() => setStep('review')}
                    />
                </FooterActions>
            );
        }

        if (step === 'review') {
            return (
                <FooterActions>
                    <Button themes={['white', 'secondary']} text="Back" type="button" disabled={isApplying} onClick={() => setStep('configure')} />
                    <Button
                        themes={['primary']}
                        text={action === 'skip' ? 'Skip email triggers' : 'Restore email triggers'}
                        type="button"
                        loading={isApplying}
                        disabled={isApplying || totalChanges === 0}
                        onClick={() => applyChanges(applicationChanges, false)}
                    />
                </FooterActions>
            );
        }

        return (
            <FooterActions>
                {result?.failures.length > 0 && (
                    <Button
                        themes={['white', 'secondary']}
                        text="Retry failed locations"
                        type="button"
                        loading={isApplying}
                        disabled={isApplying}
                        onClick={retryFailedLocations}
                    />
                )}
                {result?.failures.length === 0 && result?.policyError && (
                    <Button
                        themes={['white', 'secondary']}
                        text="Retry future-location default"
                        type="button"
                        loading={isApplying}
                        disabled={isApplying}
                        onClick={retryFutureDefaults}
                    />
                )}
                <Button themes={['primary']} text="Done" type="button" disabled={isApplying} onClick={onClose} />
            </FooterActions>
        );
    };

    return (
        <ConfirmationDialog
            title="Manage email triggers"
            warning={true}
            size="large"
            contentScrollable={true}
            isLoading={isApplying}
            onClose={isApplying ? undefined : onClose}
            customFooter={renderFooter()}
        >
            <DialogBody>
                {step === 'configure' && (
                    <>
                        <Section>
                            <SectionLabel>Action</SectionLabel>
                            <ChoiceRow role="radiogroup" aria-label="Email trigger action">
                                <ChoiceLabel isSelected={action === 'skip'}>
                                    <input
                                        type="radio"
                                        name="bulk-email-action"
                                        checked={action === 'skip'}
                                        onChange={() => setAction('skip')}
                                    />
                                    <span><strong>Skip emails</strong><small>Prevent pending emails from sending.</small></span>
                                </ChoiceLabel>
                                <ChoiceLabel isSelected={action === 'reset'}>
                                    <input
                                        type="radio"
                                        name="bulk-email-action"
                                        checked={action === 'reset'}
                                        onChange={() => setAction('reset')}
                                    />
                                    <span><strong>Restore skipped emails</strong><small>Return skipped emails to pending.</small></span>
                                </ChoiceLabel>
                            </ChoiceRow>
                        </Section>

                        <Section>
                            <ScopeHeader>
                                <div>
                                    <SectionLabel>Locations</SectionLabel>
                                    <ScopeSummary>
                                        {allAvailableLocationsSelected
                                            ? `All ${availableLocationIds.length} available locations`
                                            : `${selectedApplicationIds.length} of ${availableLocationIds.length} available locations`}
                                    </ScopeSummary>
                                    {liveLocationsCount > 0 && (
                                        <div style={{ fontSize: '12px', color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '3px 8px', marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            ✓ {liveLocationsCount} live production location{liveLocationsCount === 1 ? '' : 's'} excluded
                                        </div>
                                    )}
                                </div>
                                <PlainButton type="button" onClick={() => setIsLocationPickerOpen(open => !open)}>
                                    {isLocationPickerOpen ? 'Hide locations' : 'Choose locations'}
                                </PlainButton>
                            </ScopeHeader>
                            {locations.some(location => !!location.error || !!location.loading) && (
                                <Notice role="status">
                                    {locations.filter(location => !!location.error || !!location.loading).length} location(s) are unavailable while their onboarding status is loading or in error and cannot be selected.
                                </Notice>
                            )}
                            {allAvailableLocationsSelected && onPersistentDefaultsChange && (
                                <FutureLocationsOption>
                                    <input
                                        type="checkbox"
                                        checked={applyToFutureLocations}
                                        onChange={event => setApplyToFutureLocations(event.target.checked)}
                                    />
                                    <span>
                                        <strong>Apply to future locations</strong>
                                        <small>
                                            {action === 'skip'
                                                ? 'New locations will inherit the selected skips. You can turn this off or restore an individual location later.'
                                                : 'Also remove these skips from the default for new locations.'}
                                        </small>
                                    </span>
                                </FutureLocationsOption>
                            )}
                            {isLocationPickerOpen && (
                                <LocationPicker>
                                    <SearchInput
                                        type="search"
                                        value={locationSearch}
                                        onChange={event => setLocationSearch(event.target.value)}
                                        placeholder="Search locations..."
                                        aria-label="Search locations"
                                    />
                                    <SelectAllLocationsRow>
                                        <input
                                            ref={selectAllLocationsRef}
                                            type="checkbox"
                                            checked={allAvailableLocationsSelected}
                                            disabled={availableLocationIds.length === 0}
                                            onChange={event => setSelectedApplicationIds(event.target.checked ? availableLocationIds : [])}
                                        />
                                        <SelectAllLocationsCopy>
                                            <strong>Select all</strong>
                                            <small>Select or clear every available location</small>
                                        </SelectAllLocationsCopy>
                                        <LocationCountBadge>{availableLocationIds.length} location{availableLocationIds.length === 1 ? '' : 's'}</LocationCountBadge>
                                    </SelectAllLocationsRow>
                                    <LocationList>
                                        {filteredLocations.map(location => {
                                            const disabled = !!location.loading || !!location.error;
                                            return (
                                                <CheckboxRow key={location.application.id} isDisabled={disabled}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedApplicationIds.includes(location.application.id)}
                                                        disabled={disabled}
                                                        onChange={() => toggleLocation(location.application.id)}
                                                    />
                                                    <span>
                                                        <strong>{location.application.name}</strong>
                                                        {disabled && <small>{location.loading ? 'Loading email status' : location.error}</small>}
                                                    </span>
                                                </CheckboxRow>
                                            );
                                        })}
                                    </LocationList>
                                </LocationPicker>
                            )}
                        </Section>

                        <Section>
                            <SectionLabel>Email triggers</SectionLabel>
                            {hasOutboundLocations && (
                                <TabRow role="tablist" aria-label="Email trigger groups">
                                    <TabButton type="button" role="tab" aria-selected={activeGroup === 'inbound'} isActive={activeGroup === 'inbound'} onClick={() => setActiveGroup('inbound')}>Inbound</TabButton>
                                    <TabButton type="button" role="tab" aria-selected={activeGroup === 'outbound'} isActive={activeGroup === 'outbound'} onClick={() => setActiveGroup('outbound')}>Outbound</TabButton>
                                </TabRow>
                            )}
                            <TriggerList>
                                <TriggerSelectAll>
                                    <input
                                        ref={selectAllTriggersRef}
                                        type="checkbox"
                                        checked={allActiveSelected}
                                        disabled={selectableActiveIds.length === 0}
                                        onChange={event => setSelectedTriggerIds(current => {
                                            const withoutActive = current.filter(id => !activeDefinitions.some(definition => definition.triggerId === id));
                                            return event.target.checked ? [...withoutActive, ...selectableActiveIds] : withoutActive;
                                        })}
                                    />
                                    <span>Select all</span>
                                </TriggerSelectAll>
                                {activeDefinitions.map(definition => {
                                    const counts = triggerCounts[definition.triggerId];
                                    const disabled = !counts || counts.eligible === 0;
                                    return (
                                        <TriggerRow key={definition.triggerId} isDisabled={disabled}>
                                            <input
                                                type="checkbox"
                                                checked={selectedTriggerIds.includes(definition.triggerId)}
                                                disabled={disabled}
                                                onChange={() => toggleTrigger(definition.triggerId)}
                                                aria-label={definition.label}
                                            />
                                            <TriggerCopy>
                                                <TriggerTitleRow>
                                                    <strong>{definition.label}</strong>
                                                    <TooltipWrapper
                                                        text={definition.isAutomated
                                                            ? 'Automatic emails can send when their trigger condition is met.'
                                                            : 'Manual emails are sent by staff and do not send automatically.'}
                                                        place="top"
                                                        maxWidth={280}
                                                    >
                                                        <TriggerTypeBadge isAutomated={definition.isAutomated}>
                                                            {definition.isAutomated ? 'Automatic' : 'Manual'}
                                                        </TriggerTypeBadge>
                                                    </TooltipWrapper>
                                                </TriggerTitleRow>
                                                <small>{definition.description}</small>
                                            </TriggerCopy>
                                            <TooltipWrapper
                                                text={getActionableCountTooltip(counts, action)}
                                                place="top"
                                                maxWidth={320}
                                            >
                                                <ActionableCount hasEligibleItems={!!counts?.eligible}>
                                                    {counts?.eligible
                                                        ? `${counts.eligible} to ${action === 'skip' ? 'skip' : 'restore'}`
                                                        : `None to ${action === 'skip' ? 'skip' : 'restore'}`}
                                                </ActionableCount>
                                            </TooltipWrapper>
                                        </TriggerRow>
                                    );
                                })}
                            </TriggerList>
                            <ImpactSummary role="status">
                                <strong>{totalChanges} email trigger{totalChanges === 1 ? '' : 's'}</strong> across <strong>{applicationChanges.length} location{applicationChanges.length === 1 ? '' : 's'}</strong> will be {action === 'skip' ? 'skipped' : 'restored'}.
                                <span> Sent and otherwise ineligible email triggers will not be changed.</span>
                            </ImpactSummary>
                        </Section>
                    </>
                )}

                {step === 'review' && (
                    <ReviewPanel>
                        <ReviewHeading>Review changes</ReviewHeading>
                        <ReviewSummary>
                            <strong>{action === 'skip' ? 'Skip' : 'Restore'} {totalChanges} email trigger{totalChanges === 1 ? '' : 's'}</strong>
                            <span> across {applicationChanges.length} location{applicationChanges.length === 1 ? '' : 's'}.</span>
                        </ReviewSummary>
                        <ReviewList>
                            {locationEmailTriggers
                                .filter(definition => selectedTriggerIds.includes(definition.triggerId) && triggerCounts[definition.triggerId]?.eligible > 0)
                                .map(definition => (
                                    <ReviewRow key={definition.triggerId}>
                                        <span>{definition.label}</span>
                                        <strong>{triggerCounts[definition.triggerId].eligible}</strong>
                                    </ReviewRow>
                                ))}
                        </ReviewList>
                        <WarningNotice>
                            {action === 'skip'
                                ? 'Skipped automated emails will not send unless they are restored. Emails that were already sent will remain unchanged.'
                                : 'Restored emails return to Pending and may send when their automation conditions are met. Emails that were already sent will remain unchanged.'}
                        </WarningNotice>
                        {applyToFutureLocations && onPersistentDefaultsChange && (
                            <FutureDefaultReview>
                                {action === 'skip'
                                    ? 'Future locations will inherit these selected skips by default.'
                                    : 'These skips will also be removed from the default for future locations.'}
                            </FutureDefaultReview>
                        )}
                        {isApplying && <ProgressText role="status">Updating location {Math.min(completedCount + 1, applicationChanges.length)} of {applicationChanges.length}…</ProgressText>}
                    </ReviewPanel>
                )}

                {step === 'result' && result && (
                    <ReviewPanel>
                        <ResultHeading hasFailures={result.failures.length > 0}>
                            {result.failures.length > 0 ? 'Some locations could not be updated' : 'Email triggers updated'}
                        </ResultHeading>
                        <ReviewSummary>
                            <strong>{result.successfulApplicationIds.length} of {result.totalLocations} locations updated.</strong>
                            <span> {result.totalChanges} trigger changes were requested.</span>
                        </ReviewSummary>
                        {result.failures.length > 0 && (
                            <FailureList>
                                {result.failures.map(failure => (
                                    <li key={failure.applicationId}>
                                        <strong>{failure.applicationName}</strong>
                                        <span>{failure.error}</span>
                                    </li>
                                ))}
                            </FailureList>
                        )}
                        {result.policyError && (
                            <WarningNotice>{result.policyError}</WarningNotice>
                        )}
                        {isApplying && <ProgressText role="status">Retrying location {Math.min(completedCount + 1, result.failures.length)} of {result.failures.length}…</ProgressText>}
                    </ReviewPanel>
                )}
            </DialogBody>
        </ConfirmationDialog>
    );
};

const fontFamilyStack = `'Muli', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 8px 24px 24px;
    color: #334155;
    font-family: ${fontFamilyStack};
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-family: ${fontFamilyStack};
`;

const SectionLabel = styled.div`
    color: #1e293b;
    font-size: 14px;
    font-weight: 700;
    font-family: ${fontFamilyStack};
`;

const ChoiceRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    font-family: ${fontFamilyStack};

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const ChoiceLabel = styled.label<{ isSelected: boolean }>`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px;
    border: 1px solid ${props => props.isSelected ? '#0284c7' : '#cbd5e1'};
    border-radius: 8px;
    background: ${props => props.isSelected ? '#f0f9ff' : '#ffffff'};
    cursor: pointer;
    font-family: ${fontFamilyStack};
    transition: all 0.15s ease;

    &:hover {
        border-color: ${props => props.isSelected ? '#0284c7' : '#94a3b8'};
    }

    input { margin-top: 3px; accent-color: #0284c7; }
    span { display: flex; flex-direction: column; gap: 3px; }
    strong { font-size: 13px; color: #1e293b; }
    small { color: #64748b; font-size: 12px; }
`;

const ScopeHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const ScopeSummary = styled.div`
    margin-top: 3px;
    color: #64748b;
    font-size: 13px;
    font-family: ${fontFamilyStack};
`;

const PlainButton = styled.button`
    border: 0;
    background: transparent;
    color: #0284c7;
    padding: 6px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
        color: #0369a1;
        text-decoration: underline;
    }
`;

const Notice = styled.div`
    padding: 10px 12px;
    border-left: 3px solid #d97706;
    background: #fffbeb;
    color: #92400e;
    font-size: 12px;
    border-radius: 4px;
    font-family: ${fontFamilyStack};
`;

const FutureLocationsOption = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 13px;
    border: 1px solid #bae6fd;
    border-radius: 7px;
    background: #f0f9ff;
    cursor: pointer;
    font-family: ${fontFamilyStack};
    transition: all 0.15s ease;

    &:hover {
        border-color: #38bdf8;
        background: #e0f2fe;
    }

    input {
        width: 16px;
        height: 16px;
        margin-top: 2px;
        accent-color: #0284c7;
        cursor: pointer;
    }

    span {
        display: flex;
        flex-direction: column;
        gap: 3px;
    }

    strong {
        color: #1e3a5f;
        font-size: 13px;
    }

    small {
        color: #64748b;
        font-size: 11px;
        line-height: 1.4;
    }
`;

const LocationPicker = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    font-family: ${fontFamilyStack};
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 9px 12px;
    color: #1e293b;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
        outline: none;
        border-color: #0284c7;
        box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
`;

const LocationList = styled.div`
    max-height: 190px;
    overflow-y: auto;
    border-top: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
`;

const SelectAllLocationsRow = styled.label`
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 11px 13px;
    border: 1px solid #bae6fd;
    border-radius: 7px;
    background: #eaf7ff;
    color: #334155;
    cursor: pointer;
    font-family: ${fontFamilyStack};
    transition: background 150ms ease, border-color 150ms ease;

    &:hover {
        border-color: #38bdf8;
        background: #e0f2fe;
    }

    &:focus-within {
        outline: 2px solid #0284c7;
        outline-offset: 2px;
    }

    input {
        width: 16px;
        height: 16px;
        margin: 0;
        accent-color: #0284c7;
        cursor: pointer;
    }
`;

const SelectAllLocationsCopy = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: ${fontFamilyStack};

    strong {
        color: #1e3a5f;
        font-size: 13px;
    }

    small {
        color: #64748b;
        font-size: 11px;
    }
`;

const LocationCountBadge = styled.span`
    padding: 4px 9px;
    border-radius: 12px;
    background: #ffffff;
    color: #0369a1;
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;

    @media (max-width: 520px) {
        display: none;
    }
`;

const CheckboxRow = styled.label<{ isDisabled?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 9px 4px;
    opacity: ${props => props.isDisabled ? 0.58 : 1};
    cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
    font-family: ${fontFamilyStack};

    input { margin-top: 2px; accent-color: #0284c7; }
    span { display: flex; flex-direction: column; gap: 2px; font-size: 13px; color: #334155; }
    small { color: #b45309; font-size: 11px; }
`;

const TabRow = styled.div`
    display: flex;
    gap: 8px;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 8px;
    font-family: ${fontFamilyStack};
`;

const TabButton = styled.button<{ isActive: boolean }>`
    padding: 6px 12px;
    border: 0;
    border-radius: 6px;
    background: ${props => props.isActive ? '#0284c7' : 'transparent'};
    color: ${props => props.isActive ? '#ffffff' : '#475569'};
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${props => props.isActive ? '#0369a1' : '#f1f5f9'};
        color: ${props => props.isActive ? '#ffffff' : '#1e293b'};
    }
`;

const TriggerList = styled.div`
    border: 1px solid #dbe3eb;
    border-radius: 8px;
    overflow: hidden;
    font-family: ${fontFamilyStack};
`;

const TriggerSelectAll = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f1f5f9;
    color: #334155;
    font-family: ${fontFamilyStack};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    input { accent-color: #0284c7; }
`;

const TriggerRow = styled.label<{ isDisabled: boolean }>`
    display: grid;
    grid-template-columns: auto minmax(220px, 1fr) auto;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-top: 1px solid #e2e8f0;
    background: #ffffff;
    opacity: ${props => props.isDisabled ? 0.58 : 1};
    cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
    font-family: ${fontFamilyStack};
    transition: background 0.15s ease;

    &:hover {
        background: ${props => props.isDisabled ? '#ffffff' : '#f8fafc'};
    }

    input { accent-color: #0284c7; }

    @media (max-width: 700px) {
        grid-template-columns: auto 1fr;
    }
`;

const TriggerCopy = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-family: ${fontFamilyStack};
    small { color: #64748b; font-size: 11px; }
`;

const TriggerTitleRow = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-family: ${fontFamilyStack};

    strong {
        color: #334155;
        font-size: 13px;
    }
`;

const TriggerTypeBadge = styled.span<{ isAutomated: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border: 1px solid ${props => props.isAutomated ? '#a7f3d0' : '#ddd6fe'};
    border-radius: 10px;
    background: ${props => props.isAutomated ? '#ecfdf5' : '#f5f3ff'};
    color: ${props => props.isAutomated ? '#047857' : '#6d28d9'};
    font-family: ${fontFamilyStack};
    font-size: 10px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
`;

const ActionableCount = styled.span<{ hasEligibleItems: boolean }>`
    padding: 4px 9px;
    border-radius: 12px;
    background: ${props => props.hasEligibleItems ? '#e0f2fe' : '#f1f5f9'};
    color: ${props => props.hasEligibleItems ? '#0369a1' : '#64748b'};
    font-family: ${fontFamilyStack};
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;

    @media (max-width: 700px) {
        grid-column: 2;
        justify-self: start;
    }
`;

const ImpactSummary = styled.div`
    padding: 11px 14px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #475569;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    line-height: 1.5;
`;

const ReviewPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: ${fontFamilyStack};
`;

const ReviewHeading = styled.h3`
    margin: 0;
    color: #1e293b;
    font-family: ${fontFamilyStack};
    font-size: 18px;
    font-weight: 700;
`;

const ResultHeading = styled(ReviewHeading)<{ hasFailures: boolean }>`
    color: ${props => props.hasFailures ? '#92400e' : '#166534'};
`;

const ReviewSummary = styled.div`
    padding: 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #f8fafc;
    font-family: ${fontFamilyStack};
    font-size: 14px;
`;

const ReviewList = styled.div`
    border-top: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
`;

const ReviewRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 11px 4px;
    border-bottom: 1px solid #e2e8f0;
    font-family: ${fontFamilyStack};
    font-size: 13px;
`;

const WarningNotice = styled.div`
    padding: 12px 14px;
    border-left: 4px solid #d97706;
    background: #fffbeb;
    color: #92400e;
    border-radius: 4px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    line-height: 1.5;
`;

const FutureDefaultReview = styled.div`
    padding: 11px 14px;
    border-left: 4px solid #0284c7;
    background: #f0f9ff;
    color: #075985;
    border-radius: 4px;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    line-height: 1.5;
`;

const ProgressText = styled.div`
    color: #0369a1;
    font-family: ${fontFamilyStack};
    font-size: 13px;
    font-weight: 700;
`;

const FailureList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-family: ${fontFamilyStack};

    li {
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding: 11px 13px;
        border-left: 3px solid #dc2626;
        border-radius: 4px;
        background: #fef2f2;
        color: #7f1d1d;
        font-size: 12px;
    }
`;

const FooterActions = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    padding-right: 24px;
    font-family: ${fontFamilyStack};

    button { margin: 0; }

    @media (max-width: 700px) {
        padding-right: 12px;
    }
`;

export default BulkEmailTriggerManagerDialog;
